import { gql, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { ILab } from 'types/datatypes/ILab';

const DEFAULT_PAYMENT_TYPE_ID = '000000002';
const T300_PAYMENT_TYPE_ID = '000000005';
const ONLINE_PAYMENT_TYPE_ID = '000000004';
const TERMINAL_PAYMENT_TYPE_ID = '000000003';
const NOCASH_PAYMENT_TYPE_ID = '000000002';

const payKindIds = {
  online: ONLINE_PAYMENT_TYPE_ID,
  default: DEFAULT_PAYMENT_TYPE_ID,
  t300: T300_PAYMENT_TYPE_ID,
  terminal: TERMINAL_PAYMENT_TYPE_ID,
  nocash: NOCASH_PAYMENT_TYPE_ID
};

interface IPayKind {
  ref?: string;
  id?: string;
  name?: string;
  pay_form?: string;
  disabled?: boolean;
}

const PAYKINDS = gql(`query getPayKinds { getPayKindes { ref id pay_form name } }`);
const STORAGE_KEY = 'payKindsData';

export const usePayKinds = () => {
  // Використовуємо useSessionStorage для зберігання даних
  const [ storedData, setStoredData ] = useSessionStorage<any>(STORAGE_KEY, null);
  const { data } = useQuery(PAYKINDS, { fetchPolicy: 'cache-first' });

  useEffect(() => {
    if (data) setStoredData(data);
  }, [ data, setStoredData ]);

  // Використовуємо дані з запиту або з sessionStorage
  const sourceData = data || storedData;

  const payKinds: IPayKind[] = useMemo(
    () => sourceData?.getPayKindes?.map((r: any) => ({ ...r })) ?? []
    , [ sourceData ]
  );

  const payKindByRef = (ref: string) => payKinds?.find((r) => r.ref === ref);

  const byId = useCallback(
    (id: string) => payKinds?.find(p => p.id === id)
    , [ payKinds ]
  );

  const availablePayKinds = useCallback((lab: ILab, { disableT300 } = { disableT300: false }) => {
    let availPayKinds = payKinds.map((r) => ({
      ...r,
      disabled: r.id === payKindIds.t300
    }));

    const labPayKinds = lab?.pay_kinds?.map((r) => r.pay_kind) ?? [];
    if (labPayKinds.length) {
      availPayKinds = payKinds.map((r) => ({
        ...r,
        disabled: !labPayKinds.includes(r.ref!) || (r.id === payKindIds.t300 && disableT300),
      }));
    }
    return availPayKinds;
  }, [ payKinds ]);

  const payKindByName = useMemo(() => Object.entries(payKindIds)
    .reduce<Record<keyof typeof payKindIds, IPayKind | undefined>>(
      (acc, [ key, id ]) => {
        const kindKey = key as keyof typeof payKindIds;
        acc[kindKey] = byId(id) ?? (key === 'default' ? payKinds?.[0] : undefined);
        return acc;
      },
    {} as Record<keyof typeof payKindIds, IPayKind | undefined>
    ), [ byId, payKinds ]);

  const payKindRefs = useMemo(() => Object.entries(payKindIds).reduce<Record<keyof typeof payKindIds, string>>(
    (acc, [ key, id ]) => {
      const payKind = byId(id);
      if (payKind?.ref) {
        acc[key as keyof typeof payKindIds] = payKind.ref;
      }
      return acc;
    },
    {} as Record<keyof typeof payKindIds, string>
  ), [ byId ]);

  return {
    payKinds,
    availablePayKinds,
    payKindIds,
    payKindByName,
    payKindByRef,
    payKindRefs,
    isFromStorage: !!storedData && !data // Флаг, що показує чи дані взяті з storage
  };
};
